import { useCallback } from "react";
import debounce from "lodash/debounce";
import { useBnbSelector } from "../../redux/store";
import useGetBuildSpinnerFrame, { ImageType } from "../shared/spin/useGetBuildSpinnerFrame";
import useSpinner, {
    type GetImageSrcType,
    type UseSpinnerType,
} from "../../../../../common-deprecated/hooks/spin/useSpinner";
import { sendJsonToSalesman } from "../../../../../common-deprecated/utils";
import { SALESMAN_EXTSPIN_LOAD, SALESMAN_EXTSPIN_ANGLE } from "../../../../../common-deprecated/constants";
import { isMaster, isSlave } from "../../../../../common-deprecated/settings/utils/commonSettingUtils";

export const DEFAULT_FRAME = 3;

type UseBuildAndBuySpinType = {
    frameProps: UseSpinnerType;
    showWinterWheel: boolean;
    cacheKey: string;
    getImageSrc: (frame: number) => GetImageSrcType;
};

const useSpin = (bgColour: string): UseBuildAndBuySpinType => {
    const forcedFrame = useBnbSelector((state) => state.utils.salesmanOverrides.currentExteriorSpinFrame);
    const forceLoading = useBnbSelector((state) => state.utils.salesmanOverrides.loadExteriorSpinCacheKey);
    const settings = useBnbSelector((state) => state.commonSettings);
    const showWinterWheel = useBnbSelector((state) => state.utils.showWinterWheel);
    const showTopSpinView = useBnbSelector((state) => state.utils.showTopSpinView);
    const defaultCCISBackground = useBnbSelector((state) => state.car.model.defaultCCISBackground);

    const { getImage, cacheKey } = useGetBuildSpinnerFrame(
        showWinterWheel,
        showTopSpinView ? ImageType.Top : ImageType.Exterior,
    );
    const getImageSrcBgColour = useCallback(
        (frame: number): GetImageSrcType => ({
            src: getImage(847, 334, frame, { bgColour }),
            srcSet: `${getImage(374, 148, frame, { bgColour })} 394w, 
            ${getImage(768, 303, frame, { bgColour })} 788w, 
            ${getImage(1694, 668, frame, { bgColour })} 1714w`,
            sizes: "(min-width: 1440px) 1714px, (min-width: 768px) 788px, (min-width: 375px) 394px, 100vw",
        }),
        [cacheKey],
    );

    const getImageSrcBgImage = useCallback(
        (frame: number): GetImageSrcType => ({
            src: getImage(854, 380, frame, { bgImage: defaultCCISBackground, padding: "167,167,0,60" }),
            srcSet: `${getImage(375, 200, frame, {
                bgImage: defaultCCISBackground,
                padding: "0,0,0,0",
            })} 375w, ${getImage(854, 380, frame, {
                bgImage: defaultCCISBackground,
                padding: "167,167,0,60",
            })} 1188w, ${getImage(1708, 760, frame, {
                bgImage: defaultCCISBackground,
                padding: "334,334,0,120",
            })} 2376w, ${getImage(3843, 1710, frame, {
                bgImage: defaultCCISBackground,
                padding: "334,334,0,120",
            })} 4511w`,
            sizes: "(min-width: 2500px) 4511px, (min-width: 1200px) 2376px, (min-width: 768px) 1188px, (min-width: 375px) 375px",
        }),
        [cacheKey],
    );

    const getImageSrc = defaultCCISBackground ? getImageSrcBgImage : getImageSrcBgColour;

    const frameProps = useSpinner(getImageSrc, cacheKey, DEFAULT_FRAME);

    if (isSlave(settings)) {
        if (forceLoading === cacheKey && !frameProps.isLoading && !frameProps.isActive) {
            frameProps.preloadImages();
        } else if (forcedFrame.frame > 0 && forcedFrame.frame !== frameProps.currentFrame) {
            frameProps.setCurrentFrame(forcedFrame.frame);
        }
    } else if (isMaster(settings)) {
        if (frameProps.isLoading)
            debounce(() => {
                sendJsonToSalesman({ action: SALESMAN_EXTSPIN_LOAD, data: cacheKey });
            }, 200)();

        if (frameProps.previousImg.frame !== frameProps.currentFrame)
            debounce(() => {
                sendJsonToSalesman({
                    action: SALESMAN_EXTSPIN_ANGLE,
                    data: { key: cacheKey, frame: frameProps.currentFrame },
                });
            }, 200)();
    }

    return {
        frameProps,
        showWinterWheel,
        cacheKey,
        getImageSrc,
    };
};

export default useSpin;
